import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import toastService from '../utils/ToastService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('authToken');
            if (token) {
                await validateAndLoadUser();
            } else {
                setLoading(false);
            }
        };

        initAuth();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            if (user) {
                checkSessionTimeout();
            }
        }, 60000);

        return () => clearInterval(interval);
    }, [user]);

    const validateAndLoadUser = async () => {
        try {
            checkSessionTimeout();

            const infoRes = await axiosInstance.get('/api/v1/token/info');
            const userRes = await axiosInstance.get(`/api/v1/users/${infoRes.data.username}`);

            if (userRes.status === 200) {
                const u = userRes.data;

                const today = new Date();
                const membershipEndDate = new Date(u.membershipEndDate);
                const timeDiff = membershipEndDate.getTime() - today.getTime();
                const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) * (-1);

                if (daysDiff > 3 || u.status.id === 2) {
                    toastService.error("Abonelik süreniz bittiği için sisteme giriş yapamazsınız.", "Abonelik Hatası");
                    return false;
                } else {
                    setUser(u);
                    return true;
                }
            }
        } catch (error) {
            console.error('Token validation hatası:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const checkSessionTimeout = () => {
        const loginTime = parseInt(localStorage.getItem('loginTime') || '0');
        const currentTime = Date.now();
        const sessionDuration = 12 * 60 * 60 * 1000; // 12 saat
        const timeLeft = loginTime + sessionDuration - currentTime;

        if (timeLeft <= 5 * 60 * 1000 && timeLeft > 0) {
            toastService.warn('Oturumunuz 5 dakika içinde sonlanacak!', 'Oturum Uyarısı');
        }

        if (timeLeft <= 0) {
            logout();
        }
    };

    const login = async (username, password) => {
        try {
            setLoading(true);

            const tokenRes = await axiosInstance.post('/api/v1/auth/login', {
                username,
                password,
            });

            if (tokenRes.status === 200) {
                localStorage.setItem('authToken', tokenRes.data);
                localStorage.setItem('loginTime', Date.now().toString());

                const isValid = await validateAndLoadUser();
                if (isValid) {
                    toastService.success('Giriş başarılı! Hoş geldiniz.', 'Başarılı Giriş');
                    return true;
                }
                return false;
            } else {
                return false;
            }
        } catch (error) {
            console.error('Login hatası:', error);

            if (error.response) {
                const { status, data } = error.response;

                switch (status) {
                    case 404:
                        toastService.error(
                            data || 'Bu kullanıcı adı ile kayıtlı bir hesap bulunamadı.',
                            'Kullanıcı Bulunamadı'
                        );
                        break;
                    case 401:
                        toastService.error(
                            data || 'Hatalı şifre girişi yaptınız, lütfen tekrar deneyiniz.',
                            'Hatalı Şifre'
                        );
                        break;
                    case 403:
                        toastService.error(
                            data || 'Hesabınız aktif değil, lütfen yönetici ile iletişime geçin.',
                            'Hesap Aktif Değil'
                        );
                        break;
                    case 500:
                        toastService.error(
                            data || 'Sunucu hatası oluştu, lütfen daha sonra tekrar deneyiniz.',
                            'Sunucu Hatası'
                        );
                        break;
                    default:
                        toastService.error('Giriş yapılırken bir hata oluştu.', 'Giriş Hatası');
                }
            } else {
                toastService.error(
                    'Bağlantı hatası, lütfen internet bağlantınızı kontrol edin.',
                    'Bağlantı Hatası'
                );
            }

            throw error;
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        const theme = localStorage.getItem('theme');
        const language = localStorage.getItem('language');

        localStorage.clear();

        if (theme) localStorage.setItem('theme', theme);
        if (language) localStorage.setItem('language', language);

        setUser(null);
        toastService.info('Başarıyla çıkış yaptınız.', 'Çıkış');
        navigate('/login');
    };

    const value = {
        user,
        loading,
        login,
        logout,
        isLoggedIn: !!user,
        checkSessionTimeout
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};